import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const LOGO = 'https://cdn2.schedpilot.com/files/logo.png';

const PLATFORM_LABELS = {
  instagram: 'Instagram', twitter: 'X (Twitter)', linkedin: 'LinkedIn',
  linkedin_page: 'LinkedIn Page', facebook: 'Facebook', tiktok: 'TikTok',
  threads: 'Threads', youtube: 'YouTube', bluesky: 'Bluesky', pinterest: 'Pinterest', discord: 'Discord',
};
const PLATFORM_COLORS = {
  instagram: '#E1306C', twitter: '#000', linkedin: '#0A66C2',
  linkedin_page: '#0A66C2', facebook: '#1877F2', tiktok: '#010101',
  threads: '#000', youtube: '#FF0000', bluesky: '#0085ff', pinterest: '#E60023', discord: '#5865F2',
};

function Shell({ children }) {
  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', padding: '40px 16px 60px', fontFamily: 'system-ui, Arial, sans-serif' }}>
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
        <img src={LOGO} alt="SchedPilot" style={{ width: 36, height: 36 }} />
        <span style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1e293b' }}>SchedPilot</span>
      </div>
      <div style={{ width: '100%', maxWidth: 560 }}>{children}</div>
    </div>
  );
}

function Card({ children, style }) {
  return (
    <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,0.08)', padding: '28px 28px', marginBottom: 16, ...style }}>
      {children}
    </div>
  );
}

function MessageScreen({ icon, title, message }) {
  return (
    <Shell>
      <Card style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>{icon}</div>
        <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111', marginBottom: 8 }}>{title}</div>
        <div style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: 1.6 }}>{message}</div>
      </Card>
    </Shell>
  );
}

function ApprovalReview() {
  const { token } = useParams();

  // state: loading | ready | submitting | done | expired | already_responded | error
  const [stage, setStage]         = useState('loading');
  const [post, setPost]           = useState(null);
  const [agencyName, setAgencyName] = useState('Your Agency');
  const [respondedStatus, setRespondedStatus] = useState(null);

  // action selection
  const [chosen, setChosen]     = useState(null); // null | 'approve' | 'reject'
  const [comment, setComment]   = useState('');
  const [commentErr, setCommentErr] = useState(false);

  useEffect(() => {
    fetch(`https://api.schedpilot.com/wp-json/api/v1/review/${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.code === 200 && data.post) {
          setPost(data.post);
          setAgencyName(data.agency_name || 'Your Agency');
          setStage('ready');
        } else if (data.message === 'already_responded') {
          setRespondedStatus(data.status);
          setStage('already_responded');
        } else if (data.message === 'expired') {
          setStage('expired');
        } else {
          setStage('error');
        }
      })
      .catch(() => setStage('error'));
  }, [token]);

  const handleSubmit = async () => {
    if (chosen === 'reject' && !comment.trim()) {
      setCommentErr(true);
      return;
    }
    setStage('submitting');
    try {
      const res = await fetch(`https://api.schedpilot.com/wp-json/api/v1/review/${token}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: chosen, comment: comment.trim() }),
      }).then(r => r.json());

      if (res.code === 200) {
        setStage('done');
      } else if (res.message === 'already_responded') {
        setRespondedStatus(res.action || 'responded');
        setStage('already_responded');
      } else {
        setStage('error');
      }
    } catch {
      setStage('error');
    }
  };

  // ── Terminal states ──────────────────────────────────────────────────────────
  if (stage === 'loading') {
    return (
      <Shell>
        <Card style={{ textAlign: 'center', padding: '48px 28px' }}>
          <div style={{ width: 36, height: 36, border: '3px solid #e2e8f0', borderTopColor: '#4f46e5', borderRadius: '50%', margin: '0 auto 16px', animation: 'spin 0.8s linear infinite' }} />
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          <div style={{ color: '#64748b', fontSize: '0.9rem' }}>Loading review…</div>
        </Card>
      </Shell>
    );
  }
  if (stage === 'expired') return <MessageScreen icon="⏰" title="Link expired" message="This review link has expired. Please contact your agency to send a new one." />;
  if (stage === 'already_responded') return <MessageScreen icon="✅" title="Already responded" message={`This post has already been ${respondedStatus || 'reviewed'}. No further action is needed.`} />;
  if (stage === 'error') return <MessageScreen icon="🚫" title="Link not found" message="This review link is invalid or has already been used. Please contact your agency." />;

  if (stage === 'done') {
    return (
      <Shell>
        <Card style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 52, marginBottom: 12 }}>{chosen === 'approve' ? '✅' : '🔁'}</div>
          <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#111', marginBottom: 8 }}>
            {chosen === 'approve' ? 'Post approved!' : 'Feedback sent!'}
          </div>
          <div style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: 1.6 }}>
            {chosen === 'approve'
              ? 'The post has been approved and will be published as scheduled.'
              : 'Your feedback has been sent. The agency will review your comments and may send a revised version.'}
          </div>
        </Card>
      </Shell>
    );
  }

  // ── Main review UI ────────────────────────────────────────────────────────────
  const scheduledDate = post.scheduled_date
    ? new Date(post.scheduled_date + ' UTC').toLocaleString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null;

  const isCarousel = post.slides && post.slides.length > 0;
  const previewImages = isCarousel
    ? post.slides.map(s => s.image_url).filter(Boolean).slice(0, 6)
    : (post.images || []).slice(0, 4);

  return (
    <Shell>
      {/* Header card */}
      <Card>
        <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: 6 }}>
          Review request from <strong style={{ color: '#1e293b' }}>{agencyName}</strong>
        </div>
        <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a' }}>
          You've been asked to review a post
        </div>
        <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: 4 }}>
          Approve or reject it below. Your feedback goes straight to the agency.
        </div>
      </Card>

      {/* Post preview */}
      <Card>
        <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#94a3b8', marginBottom: 10 }}>Post Preview</div>

        {/* Accounts */}
        {(post.accounts && post.accounts.length > 0) ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            {post.accounts.map((acc, i) => {
              const color = PLATFORM_COLORS[acc.type] || '#6b7280';
              const label = PLATFORM_LABELS[acc.type] || acc.type;
              const initial = (acc.name || '?')[0].toUpperCase();
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {/* Avatar */}
                  {acc.picture ? (
                    <img src={acc.picture} alt={acc.name}
                      style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '1.5px solid #e2e8f0' }} />
                  ) : (
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                      background: color + '22', color, display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: 15,
                    }}>{initial}</div>
                  )}
                  {/* Name + platform badge */}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.875rem', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {acc.name}
                    </div>
                    <span style={{
                      display: 'inline-block', marginTop: 2,
                      background: color + '18', color,
                      padding: '1px 8px', borderRadius: 20,
                      fontSize: '0.7rem', fontWeight: 700,
                    }}>{label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Fallback: legacy platform pills if accounts not returned */
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
            {(post.platforms || []).map(p => {
              const color = PLATFORM_COLORS[p] || '#6b7280';
              return (
                <span key={p} style={{ background: color + '18', color, padding: '3px 10px', borderRadius: 10, fontSize: '0.75rem', fontWeight: 700 }}>
                  {PLATFORM_LABELS[p] || p}
                </span>
              );
            })}
          </div>
        )}

        {/* Caption */}
        {post.content && (
          <div style={{ fontSize: '0.9rem', color: '#1e293b', lineHeight: 1.65, whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginBottom: 14, padding: '12px 14px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e8edf2' }}>
            {post.content}
          </div>
        )}

        {/* Images / Carousel */}
        {previewImages.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            {isCarousel && (
              <div style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600, marginBottom: 6 }}>
                Carousel — {post.slides.length} slide{post.slides.length !== 1 ? 's' : ''}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(previewImages.length, 3)}, 1fr)`, gap: 6 }}>
              {previewImages.map((url, i) => (
                <div key={i} style={{ aspectRatio: '4/5', borderRadius: 8, overflow: 'hidden', background: '#f1f5f9' }}>
                  <img src={url} alt={`Slide ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Scheduled time */}
        {scheduledDate && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: '#64748b' }}>
            <span>🕐</span>
            <span>Scheduled for <strong style={{ color: '#1e293b' }}>{scheduledDate}</strong>
              {post.timezone ? ` (${post.timezone.split('/').pop().replace(/_/g, ' ')})` : ''}
            </span>
          </div>
        )}
      </Card>

      {/* Action area */}
      {chosen === null && (
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={() => setChosen('approve')}
            style={{ flex: 1, padding: '14px 0', borderRadius: 12, border: 'none', background: '#16a34a', color: '#fff', fontWeight: 700, fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            ✓ Approve
          </button>
          <button
            onClick={() => setChosen('reject')}
            style={{ flex: 1, padding: '14px 0', borderRadius: 12, border: 'none', background: '#dc2626', color: '#fff', fontWeight: 700, fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            ✕ Reject
          </button>
        </div>
      )}

      {chosen === 'approve' && (
        <Card>
          <div style={{ fontWeight: 700, color: '#111', marginBottom: 8 }}>Confirm approval</div>
          <div style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: 20 }}>
            The post will be published at the scheduled time. Are you sure?
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setChosen(null)}
              style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>
              Go back
            </button>
            <button onClick={handleSubmit} disabled={stage === 'submitting'}
              style={{ flex: 2, padding: '10px 0', borderRadius: 10, border: 'none', background: '#16a34a', color: '#fff', fontWeight: 700, fontSize: '0.9rem', cursor: stage === 'submitting' ? 'not-allowed' : 'pointer', opacity: stage === 'submitting' ? 0.7 : 1 }}>
              {stage === 'submitting' ? 'Approving…' : '✓ Yes, Approve'}
            </button>
          </div>
        </Card>
      )}

      {chosen === 'reject' && (
        <Card>
          <div style={{ fontWeight: 700, color: '#111', marginBottom: 4 }}>Reason for rejection</div>
          <div style={{ color: '#64748b', fontSize: '0.8rem', marginBottom: 12 }}>
            Please leave a short note so the agency knows what to change.
          </div>
          <textarea
            value={comment}
            onChange={e => { setComment(e.target.value); setCommentErr(false); }}
            placeholder="e.g. Please change the call-to-action and update the image…"
            rows={4}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: `1px solid ${commentErr ? '#ef4444' : '#e2e8f0'}`, fontSize: '0.875rem', resize: 'vertical', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
          />
          {commentErr && <div style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: 4 }}>Please add a comment before rejecting.</div>}
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <button onClick={() => setChosen(null)}
              style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>
              Go back
            </button>
            <button onClick={handleSubmit} disabled={stage === 'submitting'}
              style={{ flex: 2, padding: '10px 0', borderRadius: 10, border: 'none', background: '#dc2626', color: '#fff', fontWeight: 700, fontSize: '0.9rem', cursor: stage === 'submitting' ? 'not-allowed' : 'pointer', opacity: stage === 'submitting' ? 0.7 : 1 }}>
              {stage === 'submitting' ? 'Sending…' : '✕ Submit Rejection'}
            </button>
          </div>
        </Card>
      )}

      <div style={{ textAlign: 'center', fontSize: '0.75rem', color: '#cbd5e1', marginTop: 8 }}>
        Powered by SchedPilot · This link is single-use and expires after 7 days
      </div>
    </Shell>
  );
}

export default ApprovalReview;
